import { Warning } from '@phosphor-icons/react';
import { useId } from 'react';
import { DatePicker } from '@/components/ui/date-picker';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { ORDER_CHANNEL_LABELS, ORDER_CHANNELS } from '@brechoflow/contracts';
import { useBranches, useCategoryOptions, type DashboardFilters } from './dashboard-api';
import { PRESETS } from './kpi';

function Field({ label, htmlFor, children, className }: { label: string; htmlFor: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <label htmlFor={htmlFor} className="mb-1 block text-[0.8125rem] text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

/** Período e unidade. Tudo vive na URL (nuqs), então o link da tela é compartilhável. */
export function DashboardFiltersBar({ f }: { f: DashboardFilters }) {
  const uid = useId();
  const branches = useBranches();
  const branchOptions = (branches.data ?? []).filter((b) => b.active !== false).map((b) => ({ value: b.id, label: b.name }));
  const showBranch = branchOptions.length > 0 || Boolean(f.branchId);
  const cats = useCategoryOptions();
  const categoryOptions = (cats.data ?? []).filter((c) => c.active).map((c) => ({ value: c.id, label: c.name }));
  const channelOptions = ORDER_CHANNELS.map((c) => ({ value: c, label: ORDER_CHANNEL_LABELS[c] }));

  return (
    <form className="mb-5 flex flex-col gap-3" aria-label="Filtros do painel" onSubmit={(e) => e.preventDefault()} data-testid="dashboard-filters">
      <div role="group" aria-label="Período" className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            aria-pressed={f.preset === p.value}
            onClick={() => f.setPreset(p.value)}
            className={cn(
              'min-h-10 rounded-control border px-3.5 text-sm font-medium transition-colors',
              f.preset === p.value ? 'border-primary bg-primary text-primary-foreground' : 'border-input/60 bg-card text-foreground hover:bg-muted',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-3 lg:grid-cols-6">
        <Field label="De" htmlFor={`${uid}-from`}>
          <DatePicker id={`${uid}-from`} value={f.range.from} onChange={(v) => v && f.setFrom(v)} aria-invalid={Boolean(f.rangeError)} aria-describedby={f.rangeError ? `${uid}-err` : undefined} />
        </Field>
        <Field label="Até" htmlFor={`${uid}-to`}>
          <DatePicker id={`${uid}-to`} value={f.range.to} onChange={(v) => v && f.setTo(v)} aria-invalid={Boolean(f.rangeError)} aria-describedby={f.rangeError ? `${uid}-err` : undefined} />
        </Field>
        <Field label="Canal" htmlFor={`${uid}-channel`} className="col-span-1">
          <Select id={`${uid}-channel`} value={f.channel} onChange={f.setChannel} options={channelOptions} placeholder="Todos os canais" clearLabel="Todos os canais" />
        </Field>
        {(categoryOptions.length > 0 || Boolean(f.categoryId)) && (
          <Field label="Categoria" htmlFor={`${uid}-category`} className="col-span-1">
            <Select id={`${uid}-category`} value={f.categoryId} onChange={f.setCategory} options={categoryOptions} placeholder="Todas as categorias" clearLabel="Todas as categorias" />
          </Field>
        )}
        {showBranch && (
          <Field label="Unidade" htmlFor={`${uid}-branch`} className="col-span-2 sm:col-span-1">
            <Select id={`${uid}-branch`} value={f.branchId} onChange={f.setBranch} options={branchOptions} placeholder="Todas as unidades" clearLabel="Todas as unidades" />
          </Field>
        )}
      </div>
      {f.rangeError && (
        <p id={`${uid}-err`} role="alert" data-testid="range-error" className="flex items-center gap-2 rounded-control bg-warning-soft px-3 py-2.5 text-sm text-foreground">
          <Warning className="size-5 shrink-0 text-warning" weight="fill" aria-hidden />
          {f.rangeError} Ajuste as datas para atualizar os números.
        </p>
      )}
    </form>
  );
}
